import { API_URL } from './api';

// Интерфейс для данных пользователя
export interface User {
  id: number;
  email: string;
  username: string;
  is_active: boolean;
  is_superuser: boolean;
}

// Функция для обработки ответа от сервера
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `Ошибка ${response.status}: ${response.statusText}`);
  }
  
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  
  return response.text();
};

// Функция для регистрации пользователя
export const register = async (
  username: string,
  email: string,
  password: string
): Promise<{ access_token: string; token_type: string }> => {
  try {
    const response = await fetch(`${API_URL}/users/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        email,
        password,
      }),
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Ошибка при регистрации:', error);
    throw error;
  }
};

// Функция для авторизации пользователя
export const login = async (
  username: string,
  password: string
): Promise<boolean> => {
  try {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await fetch(`${API_URL}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    });
    
    const data = await handleResponse(response);
    
    if (data && data.access_token) {
      localStorage.setItem('token', data.access_token);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Ошибка при входе:', error);
    return false;
  }
};

// Функция для выхода из системы
export const logout = (): void => {
  localStorage.removeItem('token');
};

// Функция для получения текущего пользователя
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return null;
    }
    
    const response = await fetch(`${API_URL}/users/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    return await handleResponse(response);
  } catch (error) {
    console.error('Ошибка при получении данных пользователя:', error);
    return null;
  }
};

// Функция для проверки авторизации
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return false;
    }
    
    // Создаем контроллер для возможности отмены запроса по таймауту
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 секунд таймаут
    
    try {
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        return false;
      }
      
      return true;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
        console.error('Запрос был прерван из-за таймаута');
      } else {
        console.error('Ошибка при проверке аутентификации:', fetchError);
      }
      
      return false;
    }
  } catch (error) {
    console.error('Общая ошибка при проверке аутентификации:', error);
    return false;
  }
};

// Синхронная версия для быстрой проверки (без запроса к API)
export const isAuthenticatedSync = (): boolean => {
  return !!localStorage.getItem('token');
};

// Функция для получения токена
export const getToken = (): string | null => {
  return localStorage.getItem('token');
}; 